import React, { useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { ACCESS_STATUSES, CLIENT_PERMISSIONS } from '../../types/portal';
import type { AccessStatus, ClientPermission } from '../../types/portal';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
const PERMISSION_NOTE: Record<ClientPermission, string> = {
  View: 'Can watch and download. Cannot approve or request changes.',
  Review: 'Can watch, comment and request changes.',
  Approve: 'Can do everything, including formally approving deliverables.'
};
export function ClientAccess() {
  const {
    activeProject,
    inviteClientUser,
    updateClientUser,
    removeClientUser,
    notify
  } = usePortal();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState<ClientPermission>('Review');
  const [sent, setSent] = useState('');
  function invite(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim() || !email.trim()) return;
    inviteClientUser({
      name: name.trim(),
      email: email.trim(),
      permission
    });
    setSent(email.trim());
    notify('Invitation sent');
    setName('');
    setEmail('');
    setPermission('Review');
    setOpen(false);
  }
  return <section aria-labelledby="client-access" className="mt-16" data-id="element-176" data-mp="xcfvzxeh8ggb">
      <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-ruleDark pb-4" data-id="element-177" data-mp="12ar7eckf6z0n">
        <h2 id="client-access" className="label m-0 text-paper/40" data-id="element-178" data-mp="178anli0ohlv8">
          Client Access
        </h2>
        <p className="label-sm m-0 text-paper/35" data-id="element-179" data-mp="1c0tz89ft26xe">
          {activeProject.clientUsers.filter(user => user.status === 'Active').length} active ·
          passwordless email links
        </p>
      </div>

      <ul className="m-0 list-none p-0" data-id="element-180" data-mp="1l1s38rq8uefz">
        {activeProject.clientUsers.length === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-181" data-mp="1q0briysb82bi">
            No client users yet.
          </li>}
        {activeProject.clientUsers.map(user => <li key={user.id} className="grid grid-cols-1 items-center gap-x-8 gap-y-4 border-b border-ruleDark py-6 sm:grid-cols-12" data-id="element-182" data-mp="3i2ef1kak5id8">
            <div className="sm:col-span-4" data-id="element-183" data-mp="3r19nl312r4jp">
              <p className="font-display text-2xl leading-none" data-id="element-184" data-mp="3r087ul945tvn">{user.name}</p>
              <p className="label-sm mt-3 text-paper/40" data-id="element-185" data-mp="9k8gksagxwj4">{user.email}</p>
            </div>

            <label className="sm:col-span-3" data-id="element-186" data-mp="4l5dvehyo2eb">
              <span className="label-sm block text-paper/35" data-id="element-187" data-mp="4ob6v4tpnofb">Permission</span>
              <select value={user.permission} onChange={event => updateClientUser(user.id, {
            permission: event.target.value as ClientPermission
          })} className={SELECT} data-id="element-188" data-mp="ilg8msme614r">
                {CLIENT_PERMISSIONS.map(option => <option key={option} value={option} className={OPTION} data-id="element-189" data-mp="uvxnx7x0r328">
                    {option}
                  </option>)}
              </select>
            </label>

            <label className="sm:col-span-3" data-id="element-190" data-mp="m4fc1973v9ey">
              <span className="label-sm block text-paper/35" data-id="element-191" data-mp="m3nehsty71m1">Access</span>
              <select value={user.status} onChange={event => updateClientUser(user.id, {
            status: event.target.value as AccessStatus
          })} className={SELECT} data-id="element-192" data-mp="q4fpormdrgky">
                {ACCESS_STATUSES.map(option => <option key={option} value={option} className={OPTION} data-id="element-193" data-mp="36cokyu7hkj9">
                    {option}
                  </option>)}
              </select>
            </label>

            <div className="flex items-center gap-6 sm:col-span-2 sm:justify-end" data-id="element-194" data-mp="1xk3v3ph8noah">
              <button type="button" onClick={() => {
            setSent(user.email);
            notify('Invitation sent');
          }} className={BUTTON_QUIET} data-id="element-195" data-mp="21kxnav1prp96">
                Resend
              </button>
              <button type="button" onClick={() => {
            removeClientUser(user.id);
            notify(`${user.name} removed from this project`);
          }} className={BUTTON_QUIET} data-id="element-196" data-mp="12varof66476p">
                Remove
              </button>
            </div>

            <p className="label-sm text-paper/25 sm:col-span-12" data-id="element-197" data-mp="p2ulx6lvtv23">
              {PERMISSION_NOTE[user.permission]}
            </p>
          </li>)}
      </ul>

      {sent && <div className="mt-8 border border-paper/30 px-6 py-6" data-id="element-198" data-mp="f7ar6nj212ex">
          <p className="label m-0 text-paper" data-id="element-199" data-mp="a6wv83uq9ix9">Invitation sent</p>
          <p className="mt-4 font-display text-xl leading-snug" data-id="element-200" data-mp="a6degqgtd0kt">
            {sent} will receive a secure email with an OPEN PROJECT button. No password required.
          </p>
          <button type="button" onClick={() => setSent('')} className={`${BUTTON_QUIET} mt-5`} data-id="element-201" data-mp="2hs50vdvwcbvg">
            Dismiss
          </button>
        </div>}

      {open ? <form onSubmit={invite} className="mt-8 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-202" data-mp="2qtbjptm0pyrv">
          <h3 className="font-display text-2xl leading-none" data-id="element-203" data-mp="2vt7nbrqszo6w">Invite Client</h3>
          <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-3" data-id="element-204" data-mp="1y4xn9iakvaif">
            <label className="block" data-id="element-205" data-mp="1y6qqsopw9em7">
              <span className="label block text-paper/40" data-id="element-206" data-mp="233qx90qnojdu">Name</span>
              <input value={name} onChange={event => setName(event.target.value)} placeholder="e.g. Sarah Jones" autoFocus className={FIELD} data-id="element-207" data-mp="231xonfwhfp37" />
            </label>
            <label className="block" data-id="element-208" data-mp="1fdem7h0466j1">
              <span className="label block text-paper/40" data-id="element-209" data-mp="1fcbtd3j8du0h">Email</span>
              <input type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="e.g. sarah@example.com" className={FIELD} data-id="element-210" data-mp="1kc7vex8dbbde" />
            </label>
            <label className="block" data-id="element-211" data-mp="1m3648t0988l3">
              <span className="label block text-paper/40" data-id="element-212" data-mp="1m24m64ktmltv">Permission</span>
              <select value={permission} onChange={event => setPermission(event.target.value as ClientPermission)} className={SELECT} data-id="element-213" data-mp="1h4l1altmbasy">
                {CLIENT_PERMISSIONS.map(option => <option key={option} value={option} className={OPTION} data-id="element-214" data-mp="13c53q0jhk4zf">
                    {option}
                  </option>)}
              </select>
            </label>
          </div>
          <p className="label-sm mt-6 text-paper/30" data-id="element-215" data-mp="2erblh5s3nfg6">{PERMISSION_NOTE[permission]}</p>
          <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-216" data-mp="2et4wsydnqnui">
            <button type="submit" className={BUTTON_SOLID} data-id="element-217" data-mp="29rp7uv8czx6u">
              Send Invitation
            </button>
            <button type="button" onClick={() => setOpen(false)} className={BUTTON_QUIET} data-id="element-218" data-mp="3roiw44jhi5q7">
              Cancel
            </button>
          </div>
        </form> : <button type="button" onClick={() => setOpen(true)} className={`${BUTTON_GHOST} mt-8 inline-flex items-center gap-3`} data-id="element-219" data-mp="3iqgze16s9z8q">
          <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-220" data-mp="f8oxnv2ui0xc" />
          Invite Client
        </button>}
    </section>;
}